// @flow

import { extend, pick } from "../util/util";

import { getImage, ResourceType } from "../util/ajax";
import { Event, ErrorEvent, Evented } from "../util/evented";
import loadTileJSON from "./load_tilejson";
import { postTurnstileEvent, postMapLoadEvent } from "../util/mapbox";
import TileBounds from "./tile_bounds";
import Texture from "../render/texture";

import { cacheEntryPossiblyAdded } from "../util/tile_request_cache";
import SourceEnhance from "./source_enhance";

import type { Source } from "./source";
import type { OverscaledTileID } from "./tile_id";
import type Map from "../ui/map";
import type Dispatcher from "../util/dispatcher";
import type Tile from "./tile";
import type { Callback } from "../types/callback";
import type { Cancelable } from "../types/cancelable";
import type {
    RasterSourceSpecification,
    RasterDEMSourceSpecification,
} from "../style-spec/types";
import CRS from "../geo/crs/crs";

// mapbox-gl-修改说明：继承于souce增强类
// 修改人：龚跃健 2024年6月17日
class RasterTileSource extends SourceEnhance implements Source {
    type: "raster" | "raster-dem";
    id: string;
    minzoom: number;
    maxzoom: number;
    url: string;
    scheme: string;
    tileSize: number;

    bounds: ?[number, number, number, number];
    tileBounds: TileBounds;
    roundZoom: boolean;
    dispatcher: Dispatcher;
    // mapbox-gl-修改说明：新增crs属性
    // 修改人：龚跃健 2024年6月17日
    crs: CRS;
    tiles: Array<string>;

    _loaded: boolean;
    _options: RasterSourceSpecification | RasterDEMSourceSpecification;
    _tileJSONRequest: ?Cancelable;

    constructor(
        id: string,
        options: RasterSourceSpecification | RasterDEMSourceSpecification,
        dispatcher: Dispatcher,
        eventedParent: Evented
    ) {
        super();
        this.id = id;
        this.dispatcher = dispatcher;
        // mapbox-gl-修改说明：继承于souce增强类
        // 修改人：龚跃健 2024年6月17日
        this.crs = eventedParent.crs;
        this.setEventedParent(eventedParent);

        this.type = "raster";
        this.minzoom = 0;
        this.maxzoom = 22;
        this.roundZoom = true;
        this.scheme = "xyz";
        this.tileSize = 512;
        this._loaded = false;

        this._options = extend({ type: "raster" }, options);
        /*
         * feat(7435): 支持加密后的二三维瓦片服务接入
         * 修改说明：增加decryptOptions参数
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人: 赵凯 2025-06-05
         */
        extend(
            this,
            pick(options, ["url", "scheme", "tileSize", "decryptOptions"])
        );
    }

    load() {
        this._loaded = false;
        this.fire(new Event("dataloading", { dataType: "source" }));
        this._tileJSONRequest = loadTileJSON(
            this._options,
            this.map._requestManager,
            (err, tileJSON) => {
                this._tileJSONRequest = null;
                this._loaded = true;
                if (err) {
                    this.fire(new ErrorEvent(err));
                } else if (tileJSON) {
                    extend(this, tileJSON);
                    if (tileJSON.bounds)
                        this.tileBounds = new TileBounds(
                            tileJSON.bounds,
                            this.minzoom,
                            this.maxzoom
                        );

                    postTurnstileEvent(tileJSON.tiles);
                    postMapLoadEvent(
                        tileJSON.tiles,
                        this.map._getMapId(),
                        this.map._requestManager._skuToken
                    );

                    // `content` is included here to prevent a race condition where `Style#_updateSources` is called
                    // before the TileJSON arrives. this makes sure the tiles needed are loaded once TileJSON arrives
                    // ref: https://github.com/mapbox/mapbox-gl-js/pull/4347#discussion_r104418088
                    this.fire(
                        new Event("data", {
                            dataType: "source",
                            sourceDataType: "metadata",
                        })
                    );
                    this.fire(
                        new Event("data", {
                            dataType: "source",
                            sourceDataType: "content",
                        })
                    );
                }
            }
        );
    }

    loaded(): boolean {
        return this._loaded;
    }

    onRemove() {
        if (this._tileJSONRequest) {
            this._tileJSONRequest.cancel();
            this._tileJSONRequest = null;
        }
    }

    serialize() {
        return extend({}, this._options);
    }

    hasTile(tileID: OverscaledTileID) {
        /*
         * fix(25725): 无法正确显示mapgis发布的arcgis矢量瓦片服务
         * 修改说明：兼容TileBounds判断瓦片是否存在的逻辑，传入crs对象
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人：赵凯 2025年5月9日
         */
        return (
            !this.tileBounds ||
            this.tileBounds.contains(tileID.canonical, this.crs)
        );
    }

    loadTile(tile: Tile, callback: Callback<void>) {
        const { tileUrlReplace } = this._options;
        if (tileUrlReplace && tileUrlReplace.length >= 3) {
            tile.tileID.canonical.replaceX(tileUrlReplace[0]);
            tile.tileID.canonical.replaceY(tileUrlReplace[1]);
            tile.tileID.canonical.replaceZ(tileUrlReplace[2]);
        }
        // mapbox-gl-修改说明：raster_tile_source支持post请求资源
        // 修改人：杨婷茹 2024年5月21日
        const requestParams =
            this._options && this._options.requestParams
                ? this._options.requestParams
                : {};
        let url;
        if (requestParams && requestParams.method === "POST") {
            url = this.map._requestManager.normalizeTileURL(
                tile.tileID.canonical.url(this.tiles, this.scheme, this.crs),
                this.tileSize
            );
            const isReverse =
                url.indexOf("reversebbox=true") >= 0 ? true : false;
            requestParams.data.bbox = tile.tileID.canonical._getBBox(
                isReverse,
                this.crs
            );
        } else {
            // mapbox-gl-修改说明：将crs传入CanonicalTileID，并挂在CanonicalTileID对象上
            // 修改人：龚跃健 2024年6月12日
            url = this.map._requestManager.normalizeTileURL(
                tile.tileID.canonical.url(this.tiles, this.scheme, this.crs),
                this.tileSize
            );
        }
        /*
         * feat(7435): 支持加密后的二三维瓦片服务接入
         * 修改说明：将decryptOptions参数传入到requestParameters内，后续解密栅格瓦片时使用
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人: 赵凯 2025-06-05
         */
        const requestParameters = this.map._requestManager.transformRequest(
            url,
            ResourceType.Tile,
            requestParams
        );

        if (this.decryptOptions) {
            requestParameters.decryptOptions = this.decryptOptions;
        }

        tile.request = getImage(requestParameters, (err, img) => {
            delete tile.request;

            if (tile.aborted) {
                tile.state = "unloaded";
                callback(null);
            } else if (err) {
                tile.state = "errored";
                callback(err);
            } else if (img) {
                if (this.map._refreshExpiredTiles) tile.setExpiryData(img);
                delete (img: any).cacheControl;
                delete (img: any).expires;

                const context = this.map.painter.context;
                const gl = context.gl;
                tile.texture = this.map.painter.getTileTexture(img.width);
                if (tile.texture) {
                    tile.texture.update(img, { useMipmap: true });
                } else {
                    tile.texture = new Texture(context, img, gl.RGBA, {
                        useMipmap: true,
                    });
                    tile.texture.bind(
                        gl.LINEAR,
                        gl.CLAMP_TO_EDGE,
                        gl.LINEAR_MIPMAP_NEAREST
                    );

                    if (context.extTextureFilterAnisotropic) {
                        gl.texParameterf(
                            gl.TEXTURE_2D,
                            context.extTextureFilterAnisotropic
                                .TEXTURE_MAX_ANISOTROPY_EXT,
                            context.extTextureFilterAnisotropicMax
                        );
                    }
                }

                tile.state = "loaded";

                cacheEntryPossiblyAdded(this.dispatcher);

                callback(null);
            }
        });
    }

    abortTile(tile: Tile, callback: Callback<void>) {
        if (tile.request) {
            tile.request.cancel();
            delete tile.request;
        }
        callback();
    }

    unloadTile(tile: Tile, callback: Callback<void>) {
        if (tile.texture) this.map.painter.saveTileTexture(tile.texture);
        callback();
    }

    hasTransition() {
        return false;
    }

}

export default RasterTileSource;
